import React from 'react';
import Grid from '@material-ui/core/Grid';
import { StyleSheet, css } from 'aphrodite';
import { Line } from 'react-chartjs-2';

import { BaseEntityPage } from '../../common/components/BaseEntityPage';
import { Helper } from '../../common/helpers/Helper';
import { GraphHelper } from '../../common/components/charts/GraphHelper';
import { VenueSummary2 } from '../../components/VenueSummary2';

const queryString = require('query-string');

export class VenueHealthTimeseries extends BaseEntityPage {
  noAdd = true;

  constructor(props, options) {
    super(props, options);
    this.query = queryString.parse(props.location.search);
    this.embedded = Boolean(props.embedded);
    this.venueCode = props.venueCodeOverride ||  this.query.screen || '';
    this.pageKey = 'VenueHealthTimeseries';
    this.pageTitle = `Venue Timeseries - ${this.venueCode}`;
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: 'Venue Timeseries' },
      { title: this.venueCode }
    ];
    this.state = {
      ...this.state,
      timeseriesData: {},
      seriesItems: [],
      device: null
    };
    this.toFetch = this.makeFetches();
  }

  renderCustomReady() {
    if (!this.embedded) return null;
    return this.renderAboveTable();
  }

  makeFetches = () => {
    return [{
      key: 'venueHealthTimeseries',
      method: 'GET',
      paths: ['miscs', 'venue-health-timeseries'],
      queryParams: { venueCode: this.venueCode, limit: 24 }
    }];
  }

  onFetchSuccess(results) {
    const result = results[0] || {};
    const items = (result.items || []).slice().sort((a, b) => {
      return new Date(a.created_ts).getTime() - new Date(b.created_ts).getTime();
    });

    this.setState({
      items: [],
      timeseriesData: result,
      seriesItems: items,
      device: result.device || null
    });
  }

  formatTsLabel = (createdTs) => {
    if (!createdTs) return '';
    return Helper.formatDate2({
      utcTimestamp: new Date(createdTs).getTime(),
      format: 'MM/DD/YYYY hh:mm a',
      zone: 'Etc/UTC'
    });
    // if (!createdTs) return '';
    // console.log('createdTs', createdTs)
    // return Helper.formatTimestamp(new Date(createdTs).getTime(),);
  }

  formatTsLong = (createdTs) => {
    if (!createdTs) return '';
    return Helper.formatDate2({
      utcTimestamp: new Date(createdTs).getTime(),
      format: 'MM/dd/yyyy hh:mm a'
    });
  }

  normalizeChartValue = (value) => {
    if (value === null || value === undefined || value === '') return value;
    return value;
  }

  normalizeNetworkType = (networkType) => {
    return (networkType || '').toString().trim().toUpperCase();
  }

  getDataUsageNetworkType = (item) => {
    const networkType = this.normalizeNetworkType(item.network_type);
    if (networkType === 'WIFI') return 'wifi';
    if (networkType === 'ETHERNET') return 'ethernet';
    if (networkType === 'MOBILE' || networkType === 'CELLULAR') return 'mobile';
    return null;
  }

  getDataUsageValue = (item, direction) => {
    const networkType = this.getDataUsageNetworkType(item);
    if (!networkType) return null;
    return item[`data_${direction}_24h_type_${networkType}_gb`];
  }

  isStatusChart = (chartType) => {
    return chartType === 'connectivity_status';
  }

  getConnectivityStatusValue = (lastActivityMin) => {
    const minutes = Number(lastActivityMin);

    if (isNaN(minutes)) return null;
    if (minutes < 10) return 2;
    if (minutes < 1440) return 1;
    return 0;
  }

  getConnectivityStatusLabel = (value) => {
    if (Number(value) === 2) return 'Online';
    if (Number(value) === 1) return 'Standby';
    return 'Offline';
  }

  buildLineChart = ({ chartTitle, chartType, description, valueMapper, color }) => {
    const items = this.state.seriesItems || [];
    if (!items.length) return null;

    const labels = items.map((item) => this.formatTsLabel(item.created_ts));
    const data = items.map((item) => this.normalizeChartValue(valueMapper(item)));
    const hasValue = data.some((value) => value !== null && value !== undefined);

    if (!hasValue) return null;

    const chartData = GraphHelper.generateLineGraphData({
      chartTitle,
      labels,
      data
    });

    chartData.datasets[0].borderColor = color;
    chartData.datasets[0].backgroundColor = color;
    chartData.datasets[0].pointBackgroundColor = color;
    chartData.datasets[0].pointBorderColor = color;
    chartData.datasets[0].pointRadius = 3;
    chartData.datasets[0].pointHoverRadius = 5;
    chartData.datasets[0].borderWidth = 3;

    if (this.isStatusChart(chartType)) {
      chartData.datasets[0].steppedLine = true;
      chartData.datasets[0].pointRadius = 2;
      chartData.datasets[0].pointHoverRadius = 4;
    }

    return {
      chartTitle,
      chartType,
      description,
      latestValue: data[data.length - 1],
      data: chartData,
      options: this.getLineChartOptions(chartType)
    };
  }

  buildMultiLineChart = ({ chartTitle, description, series }) => {
    const items = this.state.seriesItems || [];
    if (!items.length) return null;

    const labels = items.map((item) => this.formatTsLabel(item.created_ts));
    const datasets = series.map((currentSeries) => {
      const data = items.map((item) => this.normalizeChartValue(currentSeries.valueMapper(item)));
      return {
        label: currentSeries.label,
        data,
        fill: false,
        borderColor: currentSeries.color,
        backgroundColor: currentSeries.color,
        pointBackgroundColor: currentSeries.color,
        pointBorderColor: currentSeries.color,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 3
      };
    }).filter((dataset) => dataset.data.some((value) => value !== null && value !== undefined));

    if (!datasets.length) return null;

    return {
      chartTitle,
      description,
      latestValue: datasets.map((dataset) => ({
        label: dataset.label,
        value: dataset.data[dataset.data.length - 1]
      })),
      data: {
        labels,
        datasets
      },
      options: this.getLineChartOptions()
    };
  }

  getLineChartOptions = (chartType) => {
    const baseOptions = GraphHelper.getLineChartOptions();
    const options = {
      ...baseOptions,
      maintainAspectRatio: false,
      legend: {
        ...baseOptions.legend,
        labels: {
          ...baseOptions.legend.labels,
          fontSize: 11
        }
      },
      layout: {
        autoPadding: false,
        padding: {
          top: 0,
          bottom: 18,
          left: 0,
          right: 10
        }
      },
      tooltips: {
        callbacks: {
          label: (tooltipItem) => {
            const rawValue = Number(tooltipItem.value || 0);
            if (this.isStatusChart(chartType)) {
              return this.getConnectivityStatusLabel(rawValue);
            }
            const datasetLabel = tooltipItem.datasetIndex !== undefined && tooltipItem.chart && tooltipItem.chart.data.datasets[tooltipItem.datasetIndex]
              ? tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].label
              : '';
            return datasetLabel ? `${datasetLabel}: ${rawValue}` : `${rawValue}`;
          }
        }
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: true,
              color: 'rgba(148, 163, 184, 0.18)',
              drawBorder: false
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 8,
              maxRotation: 0,
              minRotation: 0,
              padding: 12,
              fontSize: 10
            }
          }
        ],
        yAxes: [
          {
            gridLines: {
              display: true,
              color: 'rgba(148, 163, 184, 0.18)',
              drawBorder: false
            },
            ticks: {
              padding: 10,
              fontSize: 10,
              callback: (value) => `${value}`
            }
          }
        ]
      }
    };

    if (this.isStatusChart(chartType)) {
      options.scales.yAxes[0].ticks = {
        ...options.scales.yAxes[0].ticks,
        min: 0,
        max: 2,
        stepSize: 1,
        padding: 16,
        callback: (value) => this.getConnectivityStatusLabel(value)
      };
    } else {
      options.scales.yAxes[0].ticks = {
        ...options.scales.yAxes[0].ticks,
        precision: 2
      };
    }

    return options;
  }

  buildSummaryItems = () => {
    const items = this.state.seriesItems || [];
    const latestItem = items.length ? items[items.length - 1] : null;

    return [
      { label: 'Venue Code', value: this.venueCode || '-' },
      { label: 'Samples', value: items.length || 0 },
      { label: 'Latest Capture', value: latestItem ? this.formatTsLong(latestItem.created_ts) : '-' }
    ];
  }

  buildChartsData = () => {
    const items = this.state.seriesItems || [];
    const hasWifiSignal = items.some((item) => this.normalizeNetworkType(item.network_type) === 'WIFI' && item.wifi_signal_strength !== null && item.wifi_signal_strength !== undefined);
    const hasWifiSpeed = items.some((item) => this.normalizeNetworkType(item.network_type) === 'WIFI' && item.wifi_speed !== null && item.wifi_speed !== undefined);
    const hasCellularSignal = items.some((item) => this.normalizeNetworkType(item.network_type) === 'CELLULAR' && item.cellular_signal_strength !== null && item.cellular_signal_strength !== undefined);

    return [
      this.buildLineChart({
        chartTitle: 'Connectivity Status (Online / Standby / Offline)',
        chartType: 'connectivity_status',
        valueMapper: (item) => {
          return this.getConnectivityStatusValue(item.last_activity_min);
        },
        color: '#1d4ed8'
      }),
      this.buildMultiLineChart({
        chartTitle: 'Storage (Total / Available GB)',
        series: [
          {
            label: 'Available Storage (GB)',
            valueMapper: (item) => item.free_gb,
            color: '#ea580c'
          },
          {
            label: 'Total Storage (GB)',
            valueMapper: (item) => item.total_gb,
            color: '#2563eb'
          }
        ]
      }),
      this.buildMultiLineChart({
        chartTitle: 'RAM (Total / Available GB)',
        series: [
          {
            label: 'Available RAM (GB)',
            valueMapper: (item) => item.device_ram_avail_mem,
            color: '#059669'
          },
          {
            label: 'Total RAM (GB)',
            valueMapper: (item) => item.device_ram_total_mem,
            color: '#7c3aed'
          }
        ]
      }),
      hasWifiSignal && this.buildLineChart({
        chartTitle: 'WiFi Signal Strength (0-4 Bars)',
        valueMapper: (item) => item.wifi_signal_strength,
        color: '#0f766e'
      }),
      hasWifiSpeed && this.buildLineChart({
        chartTitle: 'WiFi Speed (0 to Max Available Mbps)',
        valueMapper: (item) => item.wifi_speed,
        color: '#b45309'
      }),
      hasCellularSignal && this.buildLineChart({
        chartTitle: 'Cellular Signal Strength (0-4 Bars)',
        valueMapper: (item) => {
          if (this.normalizeNetworkType(item.network_type) !== 'CELLULAR') return null;
          return item.cellular_signal_strength;
        },
        color: '#dc2626'
      }),
      this.buildMultiLineChart({
        chartTitle: 'Data Usage',
        series: [
          {
            label: 'Total Data (GB)',
            valueMapper: (item) => item.data_total_24h_gb,
            color: '#2563eb'
          },
          {
            label: 'Data Downloaded (GB)',
            valueMapper: (item) => this.getDataUsageValue(item, 'in'),
            color: '#059669'
          },
          {
            label: 'Data Uploaded (GB)',
            valueMapper: (item) => this.getDataUsageValue(item, 'out'),
            color: '#ea580c'
          }
        ]
      })
    ].filter(Boolean);
  }

  renderAboveTable() {
    const chartsData = this.buildChartsData();
    const { device } = this.state;

    return (
      <div className={css(Styles.mainContainer)}>
        {device &&
          <div className={css(Styles.sectionBlock)}>
            <div className={css(Styles.deviceSummaryCard)}>
              <VenueSummary2
                item={device}
                compact
                showNotRegistered={false}
              />
            </div>
          </div>
        }

        <div className={css(Styles.latencyNoteWrap)}>
          <div className={css(Styles.latencyNote)}>
            {Helper.getString('screenHealthReportLatencyNote')}
          </div>
        </div>

        <div className={css(Styles.sectionBlock)}>
          {chartsData.length > 0
            ? (
              <Grid className={css(Styles.chartsContainer)} container spacing={2}>
                {chartsData.map((chart, index) => (
                  <Grid item key={`line_chart_${index}`} className={css(Styles.eachChartHolder)} xs={12}>
                    <div className={css(Styles.chartOuterWrap)}>
                      <div className={css(Styles.chartBlock)}>
                        {chart.chartTitle &&
                          <div className={css(Styles.chartHeader)}>
                            <div className={css(Styles.chartTitleContainer)}>
                              <div className={css(Styles.chartTitle)}>{chart.chartTitle}</div>
                              <div className={css(Styles.timezoneBadge)}>CST</div>
                            </div>
                          </div>
                        }
                        <div className={css(Styles.chartCanvasHolder)}>
                          <Line
                            className={css(Styles.chartCanvas)}
                            data={chart.data}
                            options={chart.options}
                          />
                        </div>
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
            )
            : (
              <div className={css(Styles.noChartMessage)}>
                {Helper.getString('noChartDataMessage')}
              </div>
            )}
        </div>
      </div>
    );
  }
}

const Styles = StyleSheet.create({
  mainContainer: {
    width: '100%'
  },
  deviceSummaryCard: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '16px 18px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 8px 24px rgba(14, 30, 37, 0.06)'
  },
  heroBlock: {
    width: '100%',
    borderRadius: 20,
    padding: '22px',
    boxSizing: 'border-box',
    marginBottom: 24,
    background: 'linear-gradient(135deg, #f8fafc 0%, #eef4ff 52%, #fff7ed 100%)',
    border: '1px solid #e2e8f0'
  },
  heroTitleWrap: {
    marginBottom: 18
  },
  heroEyebrow: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#2563eb',
    marginBottom: 8
  },
  heroTitle: {
    fontSize: 28,
    lineHeight: 1.1,
    fontWeight: 700,
    color: '#0f172a',
    wordBreak: 'break-word'
  },
  heroText: {
    marginTop: 10,
    fontSize: 14,
    lineHeight: 1.5,
    color: '#475569',
    maxWidth: 720
  },
  summaryWrap: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
    '@media (max-width: 767px)': {
      gridTemplateColumns: '1fr'
    }
  },
  summaryCard: {
    background: 'rgba(255,255,255,0.78)',
    borderRadius: 16,
    padding: '14px 16px',
    border: '1px solid rgba(148, 163, 184, 0.2)'
  },
  summaryLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#64748b',
    marginBottom: 6
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0f172a',
    wordBreak: 'break-word'
  },
  sectionBlock: {
    width: '100%',
    marginBottom: 28
  },
  latencyNoteWrap: {
    margin: '4px 0 14px'
  },
  latencyNote: {
    fontSize: 14,
    color: '#040BC5'
  },
  chartsContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    // width: '100%'
  },
  eachChartHolder: {
    display: 'flex'
  },
  chartOuterWrap: {
    background: '#ffffff',
    borderRadius: 20,
    border: '1px solid #e5e7eb',
    boxShadow: '0 14px 32px rgba(15, 23, 42, 0.06)',
    overflow: 'hidden',
    width: '100%'
  },
  chartBlock: {
    maxHeight: '320px',
    padding: '0px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 16,
    '@media (max-width: 767px)': {
      flexDirection: 'column'
    }
  },
  chartCanvas: {
    width: '100% !important',
    height: '100% !important'
  },
  chartTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 4,
    color: '#0f172a'
  },
  chartDescription: {
    fontSize: 12,
    lineHeight: 1.35,
    color: '#64748b',
    maxWidth: 540
  },
  timezoneBadge: {
    fontSize: 12,
    fontWeight: 600,
    color: '#020BC3',
    backgroundColor: '#EEF2FF',
    borderRadius: 999,
    padding: '2px 8px',
    height: 'fit-content'
  },
  noChartMessage: {
    width: '100%',
    minHeight: 180,
    background: '#fff',
    borderRadius: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#6b7280'
  },
  chartCanvasHolder: {
    position: 'relative',
    width: '100%',
    height: 250,
    maxWidth: '100%',
    overflow: 'hidden',
    padding: '0 12px 8px 12px',
    boxSizing: 'border-box'
  },
  chartTitleContainer:{
    display: 'flex !important',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  }
});



// WEBPACK FOOTER //
// ./src/screens/admin/VenueHealthTimeseries.js